import React, { useState, useEffect } from 'react';
import { getConsent, onConsentChange, openConsentBanner } from '../utils/consent';

const consentStatusLabel = (consent) => {
  if (consent?.choice === 'accepted') return 'Du har godtatt informasjonskapsler til besøksstatistikk.';
  if (consent?.choice === 'declined') return 'Du har avslått informasjonskapsler til besøksstatistikk.';
  return 'Du har ikke tatt et valg om informasjonskapsler ennå.';
};

const Privacy = () => {
  const [consent, setConsentState] = useState(() => getConsent());

  // Keep the status line current when the user re-chooses via the banner
  useEffect(() => onConsentChange(setConsentState), []);

  return (
    <div className="max-w-[680px] mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold text-sage mb-2">Personvernerklæring</h1>
      <p className="text-sm text-text-muted mb-6">Sist oppdatert: oktober 2026</p>

      {/* Ingress */}
      <p className="text-text-primary leading-relaxed mb-10">
        Coord hjelper deg som er forelder til et barn med særskilte behov med å forstå rettigheter og skrive søknader til NAV. For å gjøre det må vi behandle opplysninger om deg og barnet ditt — også helseopplysninger. Denne erklæringen forklarer i klarspråk hva vi samler inn, hvorfor, hvem som behandler det for oss, og hvilke rettigheter du har. Kort oppsummert: opplysningene dine brukes kun til å hjelpe deg, lagres som hovedregel i datasentre innenfor EØS gjennom våre leverandører, deles aldri med NAV eller andre uten at du gjør det selv, selges aldri, og du kan når som helst slette alt selv.
      </p>

      {/* 1. Hvem som er ansvarlig */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-sage mb-3">1. Hvem som er ansvarlig</h2>
        <p className="text-text-primary leading-relaxed">
          Coord AS, org.nr. 933 971 376, er behandlingsansvarlig for personopplysningene som behandles i tjenesten. Kontakt oss om personvern på <a href="mailto:post@coord.no" className="text-primary hover:underline">post@coord.no</a>.
        </p>
      </section>

      {/* 2. Hvilke opplysninger vi behandler */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-sage mb-3">2. Hvilke opplysninger vi behandler</h2>
        <div className="space-y-3 text-text-primary leading-relaxed">
          <p>
            <span className="font-medium">Kontoopplysninger fra Vipps:</span> Når du logger inn med Vipps, mottar vi navn og e-postadresse fra Vipps etter at du har samtykket til delingen i Vipps-appen. Vi lagrer aldri passord (innlogging skjer hos Vipps), og vi ber ikke om fødselsnummer. E-postadressen og fornavnet ditt lagres i brukerprofilen din for å kunne sende deg tjenestemeldinger — som betalingskvitteringer, varsel ved mislykket betaling og viktige beskjeder om kontoen din. Opplysningene inngår i dataeksporten og slettes når du sletter kontoen.
          </p>
          <p>
            <span className="font-medium">Opplysninger du oppgir om familien:</span> Det du selv skriver inn i veiviseren og i samtaler. Dette omfatter opplysninger om barnets diagnose, funksjonsevne, hjelpebehov og hverdag, men også opplysninger om deg selv og andre familiemedlemmer som du velger å oppgi når dette er nødvendig for å gi deg relevant veiledning. Beskrivelser av slike forhold er helseopplysninger, og vi behandler dem kun med ditt uttrykkelige samtykke (se punkt 3).
          </p>
          <p>
            <span className="font-medium">Samtaler og minner:</span> Innholdet i samtalene dine med Coord, og utvalgte opplysninger fra dem som lagres som «minner» slik at hjelpen henger sammen over tid — du slipper å gjenta situasjonen din. Du kan se og slette minnene under Konto.
          </p>
          <p>
            <span className="font-medium">Søknadsutkast:</span> Utkastene tjenesten lager for deg, frem til du sletter dem.
          </p>
          <p>
            <span className="font-medium">Abonnement og betaling:</span> Status på abonnementsavtalen din, beløp og betalingshistorikk, samt de samtykkene du ga da du tegnet abonnement (med tidspunkt). Selve betalingen skjer hos Vipps — vi ser aldri kort- eller kontoopplysninger.
          </p>
          <p>
            <span className="font-medium">Hvor du kom fra (kun hvis du sier ja):</span> Kommer du til Coord via en lenke med kampanjekode (utm-parametere eller en ref-kode) og godtar besøksstatistikk, lagrer vi koden på enheten din i opptil 30 dager, sammen med hvilken side du kom inn på. Oppretter du konto i den perioden, lagres koden i brukerprofilen din — én gang, og bare det som sto i lenken. Etter innlogging slettes den fra enheten. Avslår du, lagres den verken på enheten eller i profilen, og trekker du samtykket tilbake, sletter vi den fra enheten. Vi bruker dette for å forstå hvor brukerne våre kommer fra (samtykke, GDPR art. 6 nr. 1 bokstav a). Opplysningen inngår i dataeksporten og slettes når du sletter kontoen.
          </p>
          <p>
            <span className="font-medium">Besøksstatistikk (kun hvis du sier ja):</span> Hvis du godtar informasjonskapsler til besøksstatistikk, bruker vi Google Analytics til å forstå hvordan tjenesten brukes. Sier du nei, lastes ikke verktøyet i det hele tatt. Se punkt 8.
          </p>
          <p>
            <span className="font-medium">Nyhetsbrev (kun hvis du sier ja):</span> Du kan velge å motta nyheter og tips fra Coord på e-post. Dette krever et eget samtykke, som du gir og trekker tilbake i kontoinnstillingene eller via avmeldingslenken i hver e-post — avmelding trer i kraft umiddelbart. Vi lagrer tidspunkt og versjon for samtykket ditt.
          </p>
          <p>
            <span className="font-medium">Teknisk drift:</span> Feilsøkings- og driftslogger som er renset for personinnhold — det du skriver i tjenesten havner ikke i loggene våre.
          </p>
        </div>
      </section>

      {/* 3. Hvorfor, og med hvilket rettslig grunnlag */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-sage mb-3">3. Hvorfor, og med hvilket rettslig grunnlag</h2>
        <div className="overflow-x-auto mb-3">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-text-muted border-b">
                <th className="pb-2 pr-4 font-medium">Formål</th>
                <th className="pb-2 pr-4 font-medium">Opplysninger</th>
                <th className="pb-2 font-medium">Grunnlag</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border-light">
                <td className="py-3 pr-4 text-text-primary align-top">Levere KI-assistert veiledning og søknadshjelp- og utkast, inkludert sammenhengende hjelp over tid (minner)</td>
                <td className="py-3 pr-4 text-text-primary align-top">Opplysninger om familien, helseopplysninger, samtaler og minner</td>
                <td className="py-3 text-text-primary align-top">Avtale (GDPR art. 6 nr. 1 bokstav b). Ditt uttrykkelige samtykke (GDPR art. 9 nr. 2 a), gitt før tjenesten leveres</td>
              </tr>
              <tr className="border-b border-border-light">
                <td className="py-3 pr-4 text-text-primary align-top">Levere og administrere kundeforholdet: innlogging, abonnement, betaling, støtte</td>
                <td className="py-3 pr-4 text-text-primary align-top">Kontoopplysninger, abonnements- og betalingsstatus</td>
                <td className="py-3 text-text-primary align-top">Avtale (GDPR art. 6 nr. 1 b)</td>
              </tr>
              <tr className="border-b border-border-light">
                <td className="py-3 pr-4 text-text-primary align-top">Sende tjenestemeldinger (kvitteringer, betalingsvarsler, viktige kontobeskjeder)</td>
                <td className="py-3 pr-4 text-text-primary align-top">Navn og e-postadresse</td>
                <td className="py-3 text-text-primary align-top">Avtale (art. 6 nr. 1 b)</td>
              </tr>
              <tr className="border-b border-border-light">
                <td className="py-3 pr-4 text-text-primary align-top">Sende nyhetsbrev</td>
                <td className="py-3 pr-4 text-text-primary align-top">Navn, e-postadresse og samtykkestatus</td>
                <td className="py-3 text-text-primary align-top">Samtykke (art. 6 nr. 1 a), jf. markedsføringsloven § 15</td>
              </tr>
              <tr className="border-b border-border-light">
                <td className="py-3 pr-4 text-text-primary align-top">Oppfylle lovkrav (regnskap)</td>
                <td className="py-3 pr-4 text-text-primary align-top">Betalingstransaksjoner</td>
                <td className="py-3 text-text-primary align-top">Rettslig forpliktelse (art. 6 nr. 1 c)</td>
              </tr>
              <tr className="border-b border-border-light last:border-0">
                <td className="py-3 pr-4 text-text-primary align-top">Besøksstatistikk</td>
                <td className="py-3 pr-4 text-text-primary align-top">Informasjonskapsler (GA4)</td>
                <td className="py-3 text-text-primary align-top">Samtykke (art. 6 nr. 1 a) — via banner</td>
              </tr>
              <tr className="border-b border-border-light last:border-0">
                <td className="py-3 pr-4 text-text-primary align-top">Se hvilke kanaler som fører nye familier til Coord</td>
                <td className="py-3 pr-4 text-text-primary align-top">Kampanjekode fra lenken og første side. Lagres på enheten i opptil 30 dager, og i profilen til kontoen slettes</td>
                <td className="py-3 text-text-primary align-top">Samtykke (art. 6 nr. 1 a) — via banner</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-text-primary leading-relaxed mb-3">
          Tjenesten er utviklet for foreldre eller andre med foreldreansvar som søker veiledning om rettigheter og ytelser knyttet til barnets situasjon. Opplysninger om barnet behandles derfor som en del av tjenesten etter at du uttrykkelig har samtykket til behandlingen av helseopplysninger og valgt å benytte tjenesten.
        </p>
        <p className="text-text-primary leading-relaxed">
          Du kan når som helst trekke samtykker tilbake: helseopplysninger ved å slette data under Konto, statistikk ved å endre valget ditt (punkt 8). Dersom du gjør dette, vil Coord ikke lenger kunne behandle slike opplysninger. Tilbaketrekking påvirker ikke lovligheten av behandling som har skjedd før samtykket er trukket tilbake.
        </p>
      </section>

      {/* 4. Hvordan kunstig intelligens brukes */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-sage mb-3">4. Hvordan kunstig intelligens brukes</h2>
        <p className="text-text-primary leading-relaxed">
          Coord er en KI-assistert tjeneste: når du samhandler med tjenesten, samhandler du med kunstig intelligens. Teksten du skriver behandles av språkmodeller (Microsoft Azure OpenAI, driftet i EU) for å gi svar og lage utkast, og svarene støttes av oppslag i offisielle norske kilder med kildehenvisninger. Opplysningene dine brukes ikke til å trene KI-modellene. Tjenesten benytter ikke KI eller metoder til å treffe automatiserte avgjørelser om deg eller andre registrerte: alt den lager er utkast og veiledning som du selv vurderer — det er du som eventuelt sender en søknad til NAV, utenfor tjenesten. KI kan gjøre feil, og utkast skal alltid leses gjennom før bruk.
        </p>
      </section>

      {/* 5. Hvem som behandler opplysninger for oss */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-sage mb-3">5. Hvem som behandler opplysninger for oss</h2>
        <p className="text-text-primary leading-relaxed mb-3">
          Vi deler aldri opplysningene dine med NAV, andre myndigheter eller tredjeparter for deres formål, og vi selger dem aldri. Følgende leverandører behandler opplysninger på våre vegne, regulert av databehandleravtaler:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-text-muted border-b">
                <th className="pb-2 pr-4 font-medium">Leverandør</th>
                <th className="pb-2 pr-4 font-medium">Hva de gjør for oss</th>
                <th className="pb-2 font-medium">Hvor</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border-light">
                <td className="py-3 pr-4 text-text-primary align-top">Microsoft (Azure)</td>
                <td className="py-3 pr-4 text-text-primary align-top">Drift av tjenesten: database, lagring, KI-modeller, søk og teknisk overvåking</td>
                <td className="py-3 text-text-primary align-top">Norge og Sverige (EØS)</td>
              </tr>
              <tr className="border-b border-border-light">
                <td className="py-3 pr-4 text-text-primary align-top">Vipps MobilePay AS</td>
                <td className="py-3 pr-4 text-text-primary align-top">Innlogging og betaling</td>
                <td className="py-3 text-text-primary align-top">Norge/EØS</td>
              </tr>
              <tr className="border-b border-border-light">
                <td className="py-3 pr-4 text-text-primary align-top">Google</td>
                <td className="py-3 pr-4 text-text-primary align-top">Besøksstatistikk — kun hvis du har samtykket (punkt 8). Tilbakemeldinger du sender fra tjenesten, videresendes via vår server til Google sammen med en intern bruker-ID, siden du var på og teknisk informasjon om nettleseren — aldri navn eller e-post fra kontoen din</td>
                <td className="py-3 text-text-primary align-top">EU/USA</td>
              </tr>
              <tr className="border-b border-border-light last:border-0">
                <td className="py-3 pr-4 text-text-primary align-top">Brevo (Sendinblue SAS)</td>
                <td className="py-3 pr-4 text-text-primary align-top">Utsendelse av e-post (tjenestemeldinger og nyhetsbrev). Mottar kun navn, e-postadresse og samtykkestatus — aldri helseopplysninger, samtaler eller annet innhold fra tjenesten</td>
                <td className="py-3 text-text-primary align-top">EU (Frankrike); enkelte underleverandører utenfor EØS kan benyttes i henhold til databehandleravtale med EUs standardkontraktsvilkår</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 6. Hvor lenge vi lagrer */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-sage mb-3">6. Hvor lenge vi lagrer</h2>
        <ul className="list-none space-y-1.5 text-text-primary leading-relaxed">
          <li>— Så lenge du har konto: opplysningene lagres for å gi deg tjenesten.</li>
          <li>— Sletter du selv (Konto → slett alle data), slettes alt uten ugrunnet opphold og permanent i samsvar med våre sletterutiner — også minner og søkeindekser.</li>
          <li>— Avslutter du abonnementet uten å slette, slettes opplysningene dine senest 12 måneder etter at abonnementet opphørte.</li>
          <li>— Betalingstransaksjoner oppbevares i 5 år etter bokføringsloven.</li>
        </ul>
      </section>

      {/* 7. Dine rettigheter */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-sage mb-3">7. Dine rettigheter</h2>
        <div className="space-y-3 text-text-primary leading-relaxed">
          <p>
            <span className="font-medium">Innsyn og kopi:</span> du kan når som helst eksportere dataene dine fra Konto-siden.
          </p>
          <p>
            <span className="font-medium">Retting:</span> du redigerer selv profil- og familieopplysninger i tjenesten.
          </p>
          <p>
            <span className="font-medium">Sletting:</span> selvbetjent og umiddelbar fra Konto-siden — du trenger ikke spørre oss. Du kan også slette bare minnene.
          </p>
          <p>
            <span className="font-medium">Trekke samtykke:</span> se punkt 3 og 8; nyhetsbrev melder du deg av i kontoinnstillingene eller via lenken i hver e-post.
          </p>
          <p>
            <span className="font-medium">Dataportabilitet, begrensning og innsigelse:</span> kontakt oss på <a href="mailto:post@coord.no" className="text-primary hover:underline">post@coord.no</a>, så hjelper vi deg.
          </p>
          <p>
            <span className="font-medium">Klage:</span> du kan klage til Datatilsynet (<a href="https://www.datatilsynet.no" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">datatilsynet.no</a>) hvis du mener vi behandler opplysninger i strid med regelverket — men ta gjerne kontakt med oss først. Dersom vi avslår en anmodning om innsyn eller andre rettigheter, vil du få en begrunnelse og informasjon om klageadgangen.
          </p>
        </div>
      </section>

      {/* 8. Informasjonskapsler og lokal lagring */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-sage mb-3">8. Informasjonskapsler og lokal lagring</h2>
        <p className="text-text-primary leading-relaxed mb-3">
          Innloggingen din huskes i nettleserens lokale lagring på enheten din, ikke i informasjonskapsler. Der lagrer vi også valget du tar i banneret, slik at du slipper å svare på nytt. Dette er nødvendig for at tjenesten skal virke, og kan ikke velges bort. Informasjonskapsler bruker vi bare til besøksstatistikk (Google Analytics), og bare etter at du aktivt har godtatt det i banneret. Med det samme samtykket lagrer vi kampanjekoden fra lenken du kom fra (se punkt 2). Sier du nei, lastes ikke statistikkverktøyet og kampanjekoden lagres ikke, og valget er like enkelt som å si ja. Du kan når som helst endre valget ditt her på denne siden eller under Konto → Innstillinger; trekker du samtykket, stopper målingen umiddelbart, og statistikk-informasjonskapslene og kampanjekoden slettes.
        </p>
        <p className="text-text-primary leading-relaxed mb-3">
          Skrifttypene i tjenesten lastes fra våre egne servere, ikke fra Google eller andre tredjeparter. Før du har godtatt besøksstatistikk, henter nettleseren din altså ingenting fra Google.
        </p>
        <p className="text-text-primary leading-relaxed mb-3">
          {consentStatusLabel(consent)}
        </p>
        <button
          onClick={openConsentBanner}
          className="px-4 py-2 min-h-[48px] border border-border-dark text-text-primary rounded-lg hover:bg-background-sidebar-active text-sm transition-colors"
        >
          Endre valg for informasjonskapsler
        </button>
      </section>

      {/* 9. Hvordan vi sikrer opplysningene */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-sage mb-3">9. Hvordan vi sikrer opplysningene</h2>
        <p className="text-text-primary leading-relaxed">
          Innlogging skjer med Vipps, som bekrefter identiteten din — ingen passord å miste. All kommunikasjon og lagring er kryptert, all lagring og KI-prosessering skjer innenfor EØS, tilgangen til systemene er begrenset til et minimum, og driftslogger renses automatisk for personinnhold. Skulle det likevel skje et brudd på sikkerheten som berører deg, varsler vi deg og Datatilsynet i tråd med regelverket.
        </p>
      </section>

      {/* 10. Endringer */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-sage mb-3">10. Endringer</h2>
        <p className="text-text-primary leading-relaxed">
          Ved vesentlige endringer i denne erklæringen varsler vi deg i tjenesten før endringen trår i kraft. Gjeldende versjon ligger alltid på denne siden.
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t border-border-light pt-6 text-sm text-text-muted">
        <p>Coord AS | Org.nr: 933 971 376 | <a href="mailto:post@coord.no" className="text-primary hover:underline">post@coord.no</a></p>
      </footer>
    </div>
  );
};

export default Privacy;
